import { Controller } from "@hotwired/stimulus"

// Collapsible disclosure for the readiness review cards (the warnings card and the additional-comments
// card), both of which start CLOSED. Toggling `.expanded` on the `wrap` target animates its height via a
// CSS grid-template-rows transition (0fr <-> 1fr) — see .cc-disclosure-wrap in application.tailwind.css —
// so opening/closing glides rather than jumping. A `chevron` target rotates; an `icon`/`iconExpanded`
// pair (the comments card's + / x) swaps.
//
// The comments card also collapses ITSELF after a successful "Save note": the form dispatches
// turbo:submit-end -> close-on-success, so the card animates shut once the note is persisted. "Cancel"
// calls close directly. Because we animate in place (never re-render the card on save), the typed note is
// preserved in the textarea.
export default class extends Controller {
  static targets = ["wrap", "chevron", "icon", "iconExpanded"]

  connect() {
    this.expanded = this.hasWrapTarget && this.wrapTarget.classList.contains("expanded")
    this.reflect()
  }

  toggle() {
    this.setExpanded(!this.expanded)
  }

  open() {
    this.setExpanded(true)
  }

  close() {
    this.setExpanded(false)
  }

  // For the note form's turbo:submit-end — only collapse on a successful save.
  closeOnSuccess(event) {
    if (event?.detail?.success) this.setExpanded(false)
  }

  setExpanded(expanded) {
    this.expanded = expanded
    this.reflect()
  }

  reflect() {
    if (this.hasWrapTarget) {
      this.wrapTarget.classList.toggle("expanded", this.expanded)
      // The wrap collapses to a 0fr grid row (visually hidden) but its descendants stay in the tab order
      // and a11y tree, so mark it inert while closed to keep keyboard/AT users out of hidden controls.
      this.wrapTarget.inert = !this.expanded
    }
    if (this.hasChevronTarget) this.chevronTarget.classList.toggle("rotate-180", this.expanded)
    if (this.hasIconTarget) this.iconTarget.classList.toggle("hidden", this.expanded)
    if (this.hasIconExpandedTarget) this.iconExpandedTarget.classList.toggle("hidden", !this.expanded)
    this.element.setAttribute("aria-expanded", this.expanded ? "true" : "false")
  }
}
